import React, { useEffect, useState, useRef } from "react";
import { Box, Paper, Typography, CircularProgress, Select, MenuItem, FormControl } from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { getContextAwareToken } from "../utils/storageHelper";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import HistoricalVitals from "./HistoricalVitals";

/* ================= TYPES ================= */
interface VitalsPanelProps {
  username?: string;
}

interface VitalCardProps {
  label: string;
  value: any;
  unit: string;
  icon: string;
  highlight?: boolean;
}

type VitalsSnapshot = {
  HR: any;
  SPO2: any;
  TEMP: any;
  RESP: any;
  BP: any;
  HEIGHT: any;
  WEIGHT: any;
  STEPS: any;
};




/* ---------------------------- HELPERS --------------------------- */
const safeValue = (v: any, fallback = "--") =>
  v ?? fallback;

const hasData = (v: any) => v !== null && v !== undefined && v !== "--";

/* ✅ BMI CALCULATION */
const calculateBMI = (weightLbs: number, heightCm: number) => {
  const weightKg = weightLbs * 0.453592;
  const heightM = heightCm / 100;
  return (weightKg / (heightM * heightM)).toFixed(1);
};

const getBmiStatus = (bmi: number) => {
  if (bmi < 18.5) return { label: "Underweight", color: "#3b82f6" };
  if (bmi < 25) return { label: "Normal", color: "#22c55e" };
  if (bmi < 30) return { label: "Overweight", color: "#f59e0b" };
  return { label: "Obese", color: "#ef4444" };
};

const AnimatedNumber = ({
  value,
  duration = 600,
  decimals = 0,
}: {
  value: number | string;
  duration?: number;
  decimals?: number;
}) => {
  const [display, setDisplay] = useState<number>(0);
  const prevRef = useRef<number>(0);

  useEffect(() => {
    if (typeof value !== "number") return;

    const start = prevRef.current;
    const end = value;
    const startTime = performance.now();

    const animate = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const current = start + (end - start) * progress;
      setDisplay(Number(current.toFixed(decimals)));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        prevRef.current = end;
      }
    };

    requestAnimationFrame(animate);
  }, [value, duration, decimals]);

  if (typeof value !== "number") return <>{value}</>;

  return <>{display}</>;
};


/* ============================ MAIN ============================ */

const VitalsPanel: React.FC<VitalsPanelProps> = ({ username }) => {
  const { user } = useAuth();
  const token = getContextAwareToken(user?.token, user?.role);

  const intervalRef = useRef<NodeJS.Timeout | null>(null); // ✅ HERE


  const patientusername =
    username || user?.username || localStorage.getItem("patient_username");

  const [latest, setLatest] = useState<any>({});
  const [intraday, setIntraday] = useState<any[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [steps, setSteps] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<"current" | "historical">("current");
  const [selectedDevice, setSelectedDevice] = useState<"smartwatch" | "glucometer" | "bloodpressure">("smartwatch");
  const [deviceDetails, setDeviceDetails] = useState<any>(null);
  const [deviceLoading, setDeviceLoading] = useState(false);

const latestRef = useRef<VitalsSnapshot | null>(null);
const intradayRef = useRef<any[]>([]);


  const BASE_URL = "https://vivotal-backend.vercel.app/api/v1";

const extractVitals = (data: any): VitalsSnapshot => ({
  HR: data?.HEART_RATE?.vital_data?.value,
  SPO2: data?.SPO2?.vital_data?.value,
  TEMP: data?.TEMPERATURE?.vital_data?.value,
  RESP: data?.RESPIRATORY_RATE?.vital_data?.value,
  BP: data?.BLOOD_PRESSURE?.vital_data?.value,
  HEIGHT: data?.HEIGHT?.vital_data?.value,
  WEIGHT: data?.WEIGHT?.vital_data?.value,
  STEPS: data?.STEPS?.vital_data?.value,
});


  const fetchVitals = React.useCallback(async () => {
  if (!token || !patientusername) return;

  const res = await fetch(
    `${BASE_URL}/vitals/patient/${patientusername}/latest`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  const json = await res.json();

  const mapped: any = {};
  json?.data?.vitals?.forEach((v: any) => {
    mapped[v.vital_type] = v;
  });

  const intradayHr = json?.data?.intraday_heart_rate?.heart_rate_data || [];
  const sortedHr = [...intradayHr].sort(
    (a, b) => new Date(a.takenAt).getTime() - new Date(b.takenAt).getTime()
  );

  const nextVitals = extractVitals(mapped);

  const vitalsChanged =
  !latestRef.current ||
  (Object.keys(nextVitals) as (keyof VitalsSnapshot)[]).some(
    key => latestRef.current![key] !== nextVitals[key]
  );


if (vitalsChanged) {
  latestRef.current = nextVitals;
  setLatest(mapped);
}


  if (JSON.stringify(intradayRef.current) !== JSON.stringify(sortedHr)) {
    intradayRef.current = sortedHr;
    setIntraday(sortedHr);
  }

  const stepsValue = nextVitals.STEPS ?? null;
  setSteps(prev => (prev === stepsValue ? prev : stepsValue));

  if (initialLoading) setInitialLoading(false);
}, [token, patientusername, initialLoading]);

const fetchDeviceDetails = React.useCallback(async () => {
  if (!token || !patientusername) return;

  setDeviceLoading(true);
  try {
    const endpoint = `${BASE_URL}/devices/patient/${patientusername}/${selectedDevice}`;
    const res = await fetch(endpoint, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (res.ok) {
      const json = await res.json();
      setDeviceDetails(json?.data);
    }
  } catch (error) {
    console.error("Failed to fetch device details:", error);
  } finally {
    setDeviceLoading(false);
  }
}, [token, patientusername, selectedDevice, BASE_URL]);

useEffect(() => {
  fetchDeviceDetails();
}, [fetchDeviceDetails]);






useEffect(() => {
  if (!token || !patientusername) return;

  fetchVitals();

  intervalRef.current = setInterval(fetchVitals, 60_000);

  return () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  };
}, [token, patientusername, fetchVitals]);


  // Mock steps data - in real app, fetch from API

  /* ----------------------- VALUES ----------------------- */
  const HR = safeValue(latest?.HEART_RATE?.vital_data?.value);
  const SPO2 = safeValue(latest?.SPO2?.vital_data?.value);
  const TEMP = safeValue(latest?.TEMPERATURE?.vital_data?.value);
  const RESP = safeValue(latest?.RESPIRATORY_RATE?.vital_data?.value);
  const BP = safeValue(latest?.BLOOD_PRESSURE?.vital_data?.value);

  const HEIGHT = safeValue(latest?.HEIGHT?.vital_data?.value);
  const WEIGHT = safeValue(latest?.WEIGHT?.vital_data?.value || 190);

  const BMI =
    hasData(HEIGHT) && hasData(WEIGHT)
      ? calculateBMI(Number(WEIGHT), Number(HEIGHT))
      : "--";

  const bmiValue = BMI === "--" ? null : Number(BMI);

  if (initialLoading) {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
      <CircularProgress />
    </Box>
  );
}

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Paper sx={{ p: 3, background: "#fff", borderRadius: 2, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box sx={{ p: 2, mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="h6" fontWeight={700} mb={2}>
              Vitals Overview
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select
                  value={selectedDevice}
                  onChange={(e) => setSelectedDevice(e.target.value as any)}
                  sx={{ background: "#f8fafb", borderRadius: 1, fontSize: 13, fontWeight: 600 }}
                >
                  <MenuItem value="smartwatch">Smart Watch</MenuItem>
                  <MenuItem value="glucometer">Glucometer</MenuItem>
                  <MenuItem value="bloodpressure">Blood Pressure</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1, background: "#e8f5e9", borderRadius: 1, border: "1px solid #4caf50" }}>
                <Box sx={{ width: 8, height: 8, background: "#4caf50", borderRadius: "50%" }} />
                <Box display="flex" flexDirection="column" gap={0.3}>
                  <Typography variant="caption" fontWeight={600} sx={{ color: "#2e7d32" }}>
                    {selectedDevice === "smartwatch" && "Smart Watch Connected"}
                    {selectedDevice === "glucometer" && "Glucometer Connected"}
                    {selectedDevice === "bloodpressure" && "BP Monitor Connected"}
                  </Typography>
                  {deviceDetails && (
                    <Typography variant="caption" sx={{ color: "#1b5e20", fontSize: "11px" }}>
                      {deviceDetails.model || deviceDetails.device_name || "Device"}
                      {deviceDetails.serial_number && ` • SN: ${deviceDetails.serial_number}`}
                      {deviceDetails.battery_level !== undefined && ` • 🔋 ${deviceDetails.battery_level}%`}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          
    <Box display="flex" gap={1}>
    <Typography
      onClick={() => setViewMode("current")}
      sx={{
        px: 2,
        py: 0.5,
        cursor: "pointer",
        borderRadius: 1,
        fontSize: 13,
        fontWeight: 600,
        background: viewMode === "current" ? "#2563eb" : "#f1f5f9",
        color: viewMode === "current" ? "#fff" : "#111",
      }}
    >
      Current Vitals
    </Typography>

    <Typography
      onClick={() => setViewMode("historical")}
      sx={{
        px: 2,
        py: 0.5,
        cursor: "pointer",
        borderRadius: 1,
        fontSize: 13,
        fontWeight: 600,
        background: viewMode === "historical" ? "#2563eb" : "#f1f5f9",
        color: viewMode === "historical" ? "#fff" : "#111",
      }}
    >
      Historical Data
    </Typography>
    {/* <div><Typography
            onClick={fetchVitals}
            sx={{
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 600,
              color: "#2563eb",
              "&:hover": { textDecoration: "underline" },
              opacity: initialLoading ? 0.5 : 1,
              pointerEvents: initialLoading ? "none" : "auto",
            }}
          >
            🔄 Sync
          </Typography></div> */}
  </Box>
        </Box>

        {/* DEVICE DETAILS CARD */}
        {deviceDetails && (
          <Paper sx={{ p: 2, mb: 3, background: "#f0f9ff", borderRadius: 1.5, border: "1px solid #bfdbfe" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 2 }}>
              {deviceDetails.device_name && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Device Name
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {deviceDetails.device_name}
                  </Typography>
                </Box>
              )}
              {deviceDetails.model && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Model
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {deviceDetails.model}
                  </Typography>
                </Box>
              )}
              {deviceDetails.manufacturer && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Manufacturer
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {deviceDetails.manufacturer}
                  </Typography>
                </Box>
              )}
              {deviceDetails.serial_number && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Serial Number
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {deviceDetails.serial_number}
                  </Typography>
                </Box>
              )}
              {deviceDetails.battery_level !== undefined && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Battery Level
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <Box sx={{ width: 40, height: 6, background: "#e0e0e0", borderRadius: 1, overflow: "hidden" }}>
                      <Box sx={{ width: `${deviceDetails.battery_level}%`, height: "100%", background: deviceDetails.battery_level > 20 ? "#4caf50" : "#ff9800" }} />
                    </Box>
                    <Typography variant="caption" fontWeight={600}>
                      {deviceDetails.battery_level}%
                    </Typography>
                  </Box>
                </Box>
              )}
              {deviceDetails.last_sync && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Last Sync
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {new Date(deviceDetails.last_sync).toLocaleTimeString()}
                  </Typography>
                </Box>
              )}
              {deviceDetails.status && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Status
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                    <Box sx={{ width: 8, height: 8, background: deviceDetails.status === "connected" ? "#4caf50" : "#ff9800", borderRadius: "50%" }} />
                    <Typography variant="body2" fontWeight={600} sx={{ textTransform: "capitalize" }}>
                      {deviceDetails.status}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        )}

        {deviceLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        )}
        {/* HEALTH PROGRESS STEPPER */}
        {/* <Paper sx={{ p: 2.5, background: "#f0f9ff", borderRadius: 1.5, mb: 3, border: "1px solid #bfdbfe" }}>
          <Typography variant="subtitle2" fontWeight={600} mb={2}>
            🎯 Your Health Journey
          </Typography>
          <Stepper activeStep={2}>
            <Step>
              <StepLabel>Check-in</StepLabel>
            </Step>
            <Step>
              <StepLabel>Track Vitals</StepLabel>
            </Step>
            <Step>
              <StepLabel>Stay Active</StepLabel>
            </Step>
            <Step>
              <StepLabel>Achieve Goals</StepLabel>
            </Step>
          </Stepper>
        </Paper> */}

        <Box sx={{ overflowY: "auto", flex: 1, pr: 1.5, "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { bg: "#f1f1f1", borderRadius: 1 }, "&::-webkit-scrollbar-thumb": { bg: "#888", borderRadius: 1, "&:hover": { bg: "#555" } } }}>
          {viewMode === "current" && (
            <>
              {selectedDevice === "smartwatch" && (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: 3, gridAutoRows: "min-content" }}>
            {/* LEFT SECTION */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* DAILY STEPS */}
              <Paper sx={{ p: 2, background: "#f8fafb", borderRadius: 1.5, border: "1px solid #e5edf5" }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      👟 Daily Steps
                    </Typography>
                    <Box display="flex" alignItems="baseline" gap={1} mt={1}>
                      <Typography variant="h4" fontWeight={700}>
                          {steps ?? "--"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        steps
                      </Typography>
                    </Box>
                    {/* <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                      {steps !== null ? `From ${latest?.STEPS?.device?.device_name || "Your device"}` : "No step data available"}
                    </Typography> */}
                  </Box>
                  <Typography sx={{ fontSize: "3em" }}>👣</Typography>
                </Box>
              </Paper>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2 }}>
                {/* Heart Rate & SpO2 */}
                <VitalCard
                  label="Heart Rate"
                  value={HR}
                  unit="bpm"
                  icon="❤️"
                  highlight
                />
                <VitalCard
                  label="SpO₂"
                  value={SPO2}
                  unit="%"
                  icon="💨"
                  highlight
                />


              </Box>

              {/* BMI Gauge */}
              {bmiValue && (
                <Paper sx={{ p: 2.5, background: "#f8fafb", borderRadius: 1.5, border: "1px solid #e5edf5" }}>
                  <BMIGauge bmi={bmiValue} />
                </Paper>
              )}



            </Box>

            {/* RIGHT SECTION */}
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", gap: 2 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                {/* Height & Weight */}
                <VitalCard
                  label="Height"
                  value={Number(HEIGHT).toFixed(1)}
                  unit="cm"
                  icon="📏"
                />
                <VitalCard
                  label="Weight"
                  value={WEIGHT}
                  unit="lbs"
                  icon="⚖️"
                />
                <Paper
  sx={{
    p: 2,
    background: "#f8fafb",
    border: "1px solid #e5edf5",
    borderRadius: 1.5,
  }}
>
  <Box display="flex" alignItems="center" gap={1} mb={1}>
    <Typography sx={{ fontSize: "1.4em" }}>🌡️</Typography>
    <Typography variant="caption">Skin Temperature</Typography>
  </Box>

  <Typography variant="h5">
    {TEMP === "--" && <>"--"</>}
    {TEMP !== "--" && <>{`${TEMP > 0 ? "+" : ""}${TEMP} °C`}</>}
  </Typography>
</Paper>

                <VitalCard
                  label="Blood Pressure"
                  value={BP}
                  unit=""
                  icon="💓"
                />

                <VitalCard
                  label="Respiratory Rate"
                  value={RESP}
                  unit="/brpm"
                  icon="🫁"
                />
              </Box>
            </Box>

            {/* ECG CHART - FULL WIDTH ROW */}
            <Box sx={{ gridColumn: { xs: "1", md: "1 / -1" }, mt: 1, overflow: "auto" }}>
              <Paper sx={{ p: 2.5, background: "#f8fafb", borderRadius: 1.5, border: "1px solid #e5edf5" }}>
                <Typography variant="subtitle2" fontWeight={600} mb={2}>
                  📈 Heart Rate Trend
                </Typography>
                <ECGCanvas intraday={intraday} />
              </Paper>
            </Box>
          </Box>
              )}
              {selectedDevice === "glucometer" && (
                // @ts-expect-error - patientusername can be null
                <GlucometerPanel username={patientusername} token={token} />
              )}
              {selectedDevice === "bloodpressure" && (
                // @ts-expect-error - patientusername can be null
                <BloodPressurePanel username={patientusername} token={token} />
              )}
            </>
          )}
          {viewMode === "historical" && (
            <Box>
              <HistoricalVitals username={patientusername!} token={token} />

            </Box>
          )}  
        </Box>
      </Paper>
    </Box>
  );
};

/* ============================ VITAL CARD ============================ */

const VitalCard = React.memo(
  ({ label, value, unit, icon, highlight }: VitalCardProps) => (
    <Paper
      sx={{
        p: 2,
        background: highlight ? "#f0f9ff" : "#f8fafb",
        border: highlight ? "2px solid #3b82f6" : "1px solid #e5edf5",
        borderRadius: 1.5,
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Typography sx={{ fontSize: "1.4em" }}>{icon}</Typography>
        <Typography variant="caption">{label}</Typography>
      </Box>
      <Typography variant="h5">
        <AnimatedNumber value={Number(value)} /> {unit}
      </Typography>
    </Paper>
  )
); // ✅ DOUBLE ))


/* ============================ BMI GAUGE ============================ */

const BMIGauge = ({ bmi }: { bmi: number }) => {
  const meta = getBmiStatus(bmi);
  const angle = Math.min(180, Math.max(0, ((bmi - 15) / 25) * 180));

  return (
    <Box display="flex" alignItems="center" gap={3}>
      <svg width="140" height="70" viewBox="0 0 160 80">
        <path d="M10 80 A70 70 0 0 1 50 10" stroke="#3b82f6" strokeWidth="12" fill="none" />
        <path d="M50 10 A70 70 0 0 1 110 10" stroke="#22c55e" strokeWidth="12" fill="none" />
        <path d="M110 10 A70 70 0 0 1 150 80" stroke="#f59e0b" strokeWidth="12" fill="none" />

        <line
          x1="80"
          y1="80"
          x2={80 + 50 * Math.cos((Math.PI * (180 - angle)) / 180)}
          y2={80 - 50 * Math.sin((Math.PI * (180 - angle)) / 180)}
          stroke="#111827"
          strokeWidth="3"
        />
        <circle cx="80" cy="80" r="4" fill="#111827" />
      </svg>

      <Box>
        <Typography variant="body2" color="text.secondary" mb={0.5}>
          Body Mass Index
        </Typography>
        <Box display="flex" alignItems="baseline" gap={0.5}>
          <Typography variant="h5" fontWeight={700}>
            {bmi}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            kg/m²
          </Typography>
        </Box>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ color: meta.color, mt: 0.5 }}
        >
          {meta.label}
        </Typography>
      </Box>
    </Box>
  );
};

/* ============================ ECG CHART ============================ */

const ECGCanvas = React.memo(
  ({ intraday }: { intraday: any[] }) => {
    const chartData = React.useMemo(
      () =>
        intraday.map((item) => {
          const d = new Date(item.takenAt);
          return {
            time: `${d.getHours()}:${d.getMinutes()}`,
            bpm: item.bpm || 0,
          };
        }),
      [intraday]
    );

    return (
      <Box sx={{ height: 400 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="time" />
            <YAxis />
            <Tooltip />
            <Line dataKey="bpm" stroke="#ef4444" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    );
  }
); // ✅ DOUBLE ))

/* ============================ GLUCOMETER PANEL ============================ */

interface GlucometerData {
  glucose: number;
  unit: string;
  status: string;
  trend: string;
  trendRate: number;
  device: string;
  model: string;
  timestamp: string;
  transmitterId: string;
  lowThreshold: number;
  targetHigh: number;
  targetLow: number;
  highThreshold: number;
}

const GlucometerPanel: React.FC<{ username?: any; token?: string }> = ({ username, token }) => {
  const [glucometerData, setGlucometerData] = useState<GlucometerData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGlucometerData = async () => {
      try {
        // Mock data - replace with actual API call
        const mockData: GlucometerData = {
          glucose: 103,
          unit: "mg/dL",
          status: "Normal",
          trend: "flat",
          trendRate: -0.2,
          device: "iOS - Dexcom G7",
          model: "Dexcom G7",
          timestamp: "Sep 05, 2025 5:05 PM",
          transmitterId: "4eab9f8806c3c...",
          lowThreshold: 70,
          targetLow: 70,
          targetHigh: 180,
          highThreshold: 180,
        };
        setGlucometerData(mockData);
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch glucometer data:", error);
        setLoading(false);
      }
    };

    if (username && token) {
      fetchGlucometerData();
    }
  }, [username, token]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (!glucometerData) {
    return (
      <Paper sx={{ p: 3, background: "#fff", borderRadius: 2 }}>
        <Typography color="error">No glucometer data available</Typography>
      </Paper>
    );
  }

  const getGlucoseStatus = (glucose: number) => {
    if (glucose < 70) return { label: "Low", color: "#ef4444" };
    if (glucose <= 180) return { label: "Normal", color: "#22c55e" };
    return { label: "High", color: "#f59e0b" };
  };

  const glucoseStatus = getGlucoseStatus(glucometerData.glucose);

  return (
    <Paper sx={{ p: 3, background: "#f8fafb", borderRadius: 1.5, border: "1px solid #e5edf5" }}>
      {/* Header */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mb: 3 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Vital Timestamp
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {glucometerData.timestamp}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Model
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {glucometerData.model}
          </Typography>
        </Box>
      </Box>

      {/* Glucose Reading */}
      <Box sx={{ mb: 3, p: 2.5, background: "#fff", borderRadius: 1.5, border: "2px solid #3b82f6" }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Typography sx={{ fontSize: "2em" }}>🩺</Typography>
          <Box flex={1}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Glucose
            </Typography>
            <Box display="flex" alignItems="baseline" gap={1} mt={0.5}>
              <Typography variant="h3" fontWeight={700}>
                {glucometerData.glucose}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {glucometerData.unit}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ color: glucoseStatus.color }}
        >
          {glucoseStatus.label}
        </Typography>
      </Box>

      {/* Device Information */}
      <Box sx={{ mb: 3, p: 2, background: "#fff", borderRadius: 1.5, border: "1px solid #e5edf5" }}>
        <Typography variant="subtitle2" fontWeight={600} mb={2}>
          Device Information
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Device
            </Typography>
            <Typography variant="body2">{glucometerData.device}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Transmitter ID
            </Typography>
            <Typography variant="body2">{glucometerData.transmitterId}</Typography>
          </Box>
        </Box>
      </Box>

      {/* Trend Information */}
      <Box sx={{ mb: 3, p: 2, background: "#fff", borderRadius: 1.5, border: "1px solid #e5edf5" }}>
        <Typography variant="subtitle2" fontWeight={600} mb={2}>
          Trend
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Trend
            </Typography>
            <Typography variant="body2" fontWeight={600}>{glucometerData.trend}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Trend Rate
            </Typography>
            <Typography variant="body2">{glucometerData.trendRate}</Typography>
          </Box>
        </Box>
      </Box>

      {/* Thresholds */}
      <Box sx={{ p: 2, background: "#fff", borderRadius: 1.5, border: "1px solid #e5edf5" }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Thresholds: Low &lt;{glucometerData.lowThreshold} • Target {glucometerData.targetLow}-{glucometerData.targetHigh} • High &gt;{glucometerData.highThreshold}
        </Typography>
      </Box>
    </Paper>
  );
};

/* ============================ BLOOD PRESSURE PANEL ============================ */

interface BloodPressureData {
  systolic: number;
  diastolic: number;
  status: string;
  device: string;
  timestamp: string;
  pulse: number;
}

const BloodPressurePanel: React.FC<{ username?: any; token?: string }> = ({ username, token }) => {
  const [bpData, setBpData] = useState<BloodPressureData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBPData = async () => {
      try {
        // Mock data - replace with actual API call
        const mockData: BloodPressureData = {
          systolic: 128,
          diastolic: 82,
          status: "Normal",
          device: "Omron BP Monitor",
          timestamp: "Jan 12, 2026 2:30 PM",
          pulse: 72,
        };
        setBpData(mockData);
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch blood pressure data:", error);
        setLoading(false);
      }
    };

    if (username && token) {
      fetchBPData();
    }
  }, [username, token]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (!bpData) {
    return (
      <Paper sx={{ p: 3, background: "#fff", borderRadius: 2 }}>
        <Typography color="error">No blood pressure data available</Typography>
      </Paper>
    );
  }

  const getBPStatus = (systolic: number, diastolic: number) => {
    if (systolic < 120 && diastolic < 80) return { label: "Normal", color: "#22c55e" };
    if (systolic < 130 && diastolic < 80) return { label: "Elevated", color: "#f59e0b" };
    if (systolic < 140 || diastolic < 90) return { label: "Stage 1 Hypertension", color: "#f59e0b" };
    return { label: "Stage 2 Hypertension", color: "#ef4444" };
  };

  const bpStatus = getBPStatus(bpData.systolic, bpData.diastolic);

  return (
    <Paper sx={{ p: 3, background: "#f8fafb", borderRadius: 1.5, border: "1px solid #e5edf5" }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Measurement Time
        </Typography>
        <Typography variant="body2" fontWeight={600}>
          {bpData.timestamp}
        </Typography>
      </Box>

      {/* Blood Pressure Reading */}
      <Box sx={{ mb: 3, p: 2.5, background: "#fff", borderRadius: 1.5, border: "2px solid #3b82f6" }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Typography sx={{ fontSize: "2em" }}>💓</Typography>
          <Box flex={1}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Blood Pressure
            </Typography>
            <Box display="flex" alignItems="baseline" gap={2} mt={1}>
              <Box>
                <Box display="flex" alignItems="baseline" gap={0.5}>
                  <Typography variant="h3" fontWeight={700}>
                    {bpData.systolic}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    mmHg
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Systolic
                </Typography>
              </Box>
              <Typography variant="h5" color="text.secondary" sx={{ mb: 1 }}>
                /
              </Typography>
              <Box>
                <Box display="flex" alignItems="baseline" gap={0.5}>
                  <Typography variant="h3" fontWeight={700}>
                    {bpData.diastolic}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    mmHg
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Diastolic
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ color: bpStatus.color }}
        >
          {bpStatus.label}
        </Typography>
      </Box>

      {/* Pulse */}
      <Box sx={{ mb: 3, p: 2.5, background: "#fff", borderRadius: 1.5, border: "1px solid #e5edf5" }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography sx={{ fontSize: "1.8em" }}>💗</Typography>
          <Box flex={1}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Pulse Rate
            </Typography>
            <Box display="flex" alignItems="baseline" gap={1} mt={0.5}>
              <Typography variant="h5" fontWeight={700}>
                {bpData.pulse}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                bpm
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Device Information */}
      <Box sx={{ p: 2, background: "#fff", borderRadius: 1.5, border: "1px solid #e5edf5" }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Device
        </Typography>
        <Typography variant="body2">{bpData.device}</Typography>
      </Box>
    </Paper>
  );
};

export default VitalsPanel;
